import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lock, Unlock, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { AdminSection } from "./AdminSection";

const ADMIN_SESSION_KEY = "admin_unlocked";

export const AdminPasswordGate = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Check if already unlocked in this session
  useEffect(() => {
    const unlocked = sessionStorage.getItem(ADMIN_SESSION_KEY);
    if (unlocked === "true") {
      setIsUnlocked(true);
    }
  }, []);

  const handleUnlock = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-admin-password", {
        body: { password },
      });

      if (error) throw error;

      if (data.success) {
        setIsUnlocked(true);
        sessionStorage.setItem(ADMIN_SESSION_KEY, "true");
        setShowPasswordDialog(false);
        setPassword("");
        toast({
          title: "Admin-Bereich freigeschaltet",
          description: "Du hast jetzt Zugriff auf den Admin-Bereich.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Falsches Passwort",
          description: "Bitte versuche es erneut.",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Passwort konnte nicht überprüft werden.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLock = () => {
    setIsUnlocked(false);
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    toast({
      title: "Admin-Bereich gesperrt",
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleUnlock();
    }
  };

  return (
    <div className="bg-[#243949]">
      {isUnlocked ? (
        <>
          <AdminSection />
          <div className="container mx-auto px-4 pb-8">
            <Button
              onClick={handleLock}
              variant="outline"
              className="w-full max-w-lg mx-auto flex items-center justify-center gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <Lock className="w-4 h-4" />
              Admin-Bereich sperren
            </Button>
          </div>
        </>
      ) : (
        <div className="container mx-auto px-4 py-8">
          <Button
            onClick={() => setShowPasswordDialog(true)}
            variant="outline"
            className="w-full max-w-lg mx-auto flex items-center justify-center gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <Unlock className="w-4 h-4" />
            Admin-Bereich
          </Button>
        </div>
      )}

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-[#243949] border-white/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Admin-Passwort eingeben
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              type="password"
              placeholder="Passwort"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                onClick={handleUnlock}
                disabled={isLoading || !password}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {isLoading ? "Prüfe..." : "Freischalten"}
              </Button>
              <Button
                onClick={() => {
                  setShowPasswordDialog(false);
                  setPassword("");
                }}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
